import React, { useEffect, useRef, useState } from "react";
import ReportLayout from "../components/ReportLayout";
import { Button } from "react-bootstrap";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getPrReportSheetTitleForMonth } from "../helpers/prReportGoogleSheet";
import { TIMEZONE } from "../constans";
import { authFetch } from "../api/authFetch";

dayjs.extend(utc);
dayjs.extend(timezone);

const presetPRPlus = {
    label: "PR+",
    sources: [1, 9, 10],
    statuses: [1, 2, 3, 4, 5, 6],
    selectedManagers: [],
    tagFilterMode: "no_both",
    rangeDate: "day",
    fetchLiveToday: true,
    showMonthTurnoverBar: true,
    showDirectManagerDialogConversion: true,
    showOfflineManagerStats: true,
    showManagerScheduleWorkingDays: true,
    /** Після завантаження Summary — синк погоди Open-Meteo у БД для дат з Summary. */
    syncKyivWeather: true,
    columns: {
        date: true,             // Період
        kyivWeather: true,     // Погода Київ (Open-Meteo → Summary)
        ordersCount: true,      // Кількість замовлень
        directManager: true,   // зі сторінки «Ліди» (Summary.directManager)
        instagramFollowers: true, // підписники IG з Summary (крон) + зміна до попереднього дня
        leads: true,            // Ліди
        tickets: true,          // Діалоги
        total: true,            // Сума замовлень
        margin: true,             // Маржа
        cost: true,             // Собівартість
        payments: true,         // Сума оплат
        avgCheck: true,         // Середній чек
        ads: true,              // Кількість РК
        spend: true,            // Рекламний бюджет

        clicks: true,           // Кількість кліків
        impressions: true,      // Кількість показів
        upsales: false,          // Допродаж
        repeatSales: true,      // Повторні продажі
        saleOrders: true,       // Розпродажі
        itemsCount: true,       // Кількість речей
        productBreakdown: true  // Колонки с названиями товаров
    },
    range: {
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("month").toDate()
    }
};

const ReportsPRTable = ({user}) => {
    const [showExportBlock, setShowExportBlock] = useState(false);
    const [reportLayoutLoading, setReportLayoutLoading] = useState(false);
    const [reportDataLoading, setReportDataLoading] = useState(false);
    const [reportInitLoading, setReportInitLoading] = useState(false);
    const [updateMessage, setUpdateMessage] = useState("");
    const [storedSummaries, setStoredSummaries] = useState([]);
    const [productSpendList, setProductSpendList] = useState([]);
    const [showProductTables, setShowProductTables] = useState(false);
    const [googleSheetsTodayHint, setGoogleSheetsTodayHint] = useState(null);
    const [range, setRange] = useState({
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("month").toDate()
    });
    const [igProfile, setIgProfile] = useState({ status: "idle", data: null, message: "" });
    const [igDirectLoading, setIgDirectLoading] = useState(null); // null | "preview" | "sync"
    const [igDirectHint, setIgDirectHint] = useState(null); // { type, message }
    const [agentLoading, setAgentLoading] = useState(null); // null | "daily" | "weekly"
    const [agentHint, setAgentHint] = useState(null); // { type, message }
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [backfillHint, setBackfillHint] = useState(null); // { type, message }
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isAdmin = user?.role === "admin";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setIgProfile((s) => ({ ...s, status: "loading" }));
            try {
                const res = await fetch("/api/instagram/me-stats");
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (!res.ok) {
                    setIgProfile({
                        status: "error",
                        data: null,
                        message: data.message || `HTTP ${res.status}`,
                    });
                    return;
                }
                setIgProfile({ status: "ok", data, message: "" });
            } catch (e) {
                if (!cancelled) {
                    setIgProfile({
                        status: "error",
                        data: null,
                        message: e.message || "Мережа",
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const formatDirectResultLine = (r) => {
        if (!r || typeof r !== "object") return "";
        if (r.newThreadsToday != null && r.activeThreadsToday != null) {
            return `нових тредів: ${r.newThreadsToday}, активних сьогодні: ${r.activeThreadsToday} · конверсацій: ${r.conversationsTotal ?? "—"}, кандидатів: ${r.candidates ?? "—"}, Mongo merge: ${r.statesMerged ?? "—"} · дата (Київ): ${r.dateKyiv || r.date || "—"}`;
        }
        if (r.leadsInstagram != null && r.ticketsInstagram != null) {
            return `Summary: ліди IG ${r.leadsInstagram}, діалоги IG ${r.ticketsInstagram} · ${r.date || "—"} · matched ${r.matched ?? r.matchedCount ?? "—"}, modified ${r.modified ?? r.modifiedCount ?? "—"}`;
        }
        return r.message || JSON.stringify(r).slice(0, 200);
    };

    const handleDirectPreview = async () => {
        setIgDirectHint(null);
        setIgDirectLoading("preview");
        try {
            const res = await fetch("/api/instagram/direct/stats-preview", {
                credentials: "include",
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg =
                    data.message ||
                    (data.result && (data.result.message || formatDirectResultLine(data.result))) ||
                    `HTTP ${res.status}`;
                setIgDirectHint({ type: "error", message: `Direct прев'ю: ${msg}` });
                return;
            }
            const line = formatDirectResultLine(data.result);
            const noteExtra =
                typeof data.note === "string" && data.note.length > 0 ? ` ${data.note}` : "";
            setIgDirectHint({
                type: "success",
                message: `Direct прев'ю (Summary не змінено). ${line}.${noteExtra}`,
            });
        } catch (e) {
            setIgDirectHint({
                type: "error",
                message: `Direct прев'ю: ${e.message || "мережа"}`,
            });
        } finally {
            setIgDirectLoading(null);
        }
    };

    const handleDirectSyncToSummary = async () => {
        setIgDirectHint(null);
        setIgDirectLoading("sync");
        try {
            const res = await fetch("/api/instagram/direct/sync-to-summary", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg =
                    data.message ||
                    (data.result && (data.result.message || formatDirectResultLine(data.result))) ||
                    `HTTP ${res.status}`;
                setIgDirectHint({ type: "error", message: `Direct → Summary: ${msg}` });
                return;
            }
            const line = formatDirectResultLine(data.result);
            setIgDirectHint({
                type: "success",
                message: `Direct записано в Summary. ${line}`,
            });
        } catch (e) {
            setIgDirectHint({
                type: "error",
                message: `Direct → Summary: ${e.message || "мережа"}`,
            });
        } finally {
            setIgDirectLoading(null);
        }
    };

    const topToolbarExtra = (
        <div className="ms-md-auto d-flex flex-column align-items-end gap-2 small text-muted w-100 w-md-auto">
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                {igProfile.status === "loading" && <span>Instagram…</span>}
                {igProfile.status === "error" && (
                    <span className="text-danger-emphasis" title={igProfile.message}>
                        Instagram: помилка
                    </span>
                )}
                {igProfile.status === "ok" && igProfile.data && (
                    <>
                        <span className="text-nowrap">
                            📷 @{igProfile.data.username || "—"}
                        </span>
                        <span className="text-dark fw-semibold text-nowrap">
                            {typeof igProfile.data.followersCount === "number"
                                ? `${new Intl.NumberFormat("uk-UA").format(
                                      igProfile.data.followersCount
                                  )} підписників`
                                : "підписників: —"}
                        </span>
                    </>
                )}
            </div>
            {isAdmin && (
                <div className="d-flex flex-column align-items-stretch gap-2 w-100 border-top border-secondary-subtle pt-2">
                    <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
                        <span className="text-muted text-nowrap">Instagram Direct (Київ, сьогодні):</span>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-secondary"
                            disabled={igDirectLoading === "preview"}
                            onClick={handleDirectPreview}
                        >
                            {igDirectLoading === "preview" ? "…" : "Прев'ю"}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            disabled={igDirectLoading === "sync"}
                            onClick={handleDirectSyncToSummary}
                        >
                            {igDirectLoading === "sync" ? "…" : "У Summary"}
                        </Button>
                    </div>
                    {igDirectHint?.message && (
                        <div
                            className={`small rounded px-2 py-1 ${
                                igDirectHint.type === "success"
                                    ? "bg-success-subtle text-success-emphasis"
                                    : "bg-danger-subtle text-danger-emphasis"
                            }`}
                        >
                            {igDirectHint.message}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const handleExportClick = () => {
        setShowExportBlock(prev => !prev);
    };

    const handleSummariesLoaded = (data) => {
        setStoredSummaries(data);
        console.log("data", data);
        const productSpendMap = {};

        data.forEach(summary => {
            (summary.campaigns || []).forEach(campaign => {
                const rawName = campaign.campaign_name || "";
                const spend = parseFloat(campaign.spend || 0);

                const cleaned = rawName.replace(/\(.*?\)/g, "");
                const names = cleaned.split(",").map(n => n.trim()).filter(n => n.length > 0);

                names.forEach(name => {
                    if (!productSpendMap[name]) {
                        productSpendMap[name] = 0;
                    }
                    productSpendMap[name] += spend;
                });
            });
        });

        const result = Object.entries(productSpendMap).map(([name, spend]) => ({
            name,
            spend: Number(spend.toFixed(2))
        }));

        console.log("📦 Витрати за товарами:", result);
    };

    const handleShowAllCampaigns = () => {
        const campaigns = storedSummaries.flatMap(summary =>
            (summary.campaigns || []).map(camp => ({
                id: camp.campaign_id,
                name: camp.campaign_name || "",
                spend: parseFloat(camp.spend || 0)
            }))
        );

        const namesById = {};
        campaigns.forEach(c => {
            if (!namesById[c.id]) namesById[c.id] = new Set();
            namesById[c.id].add(c.name);
        });

        const renameMap = {};
        for (const [id, namesSet] of Object.entries(namesById)) {
            const names = Array.from(namesSet);
            const preferred = names.find(n => !n.toLowerCase().includes("instagram"));
            if (preferred) {
                renameMap[id] = preferred;
            }
        }

        const updatedCampaigns = campaigns.map(c => {
            const newName = renameMap[c.id] || c.name;
            return {
                ...c,
                name: newName
            };
        });

        const productSpendMap = {};

        updatedCampaigns.forEach(c => {
            const cleaned = c.name.replace(/\(.*?\)/g, "");
            const productNames = cleaned
                .split(",")
                .map(p => p.trim())
                .filter(p => p.length > 0);

            const portion = c.spend / productNames.length;

            productNames.forEach(product => {
                if (!productSpendMap[product]) {
                    productSpendMap[product] = 0;
                }
                productSpendMap[product] += portion;
            });
        });

        const result = Object.entries(productSpendMap).map(([name, spend]) => ({
            name,
            spend: Number(spend.toFixed(2))
        }));

        console.log("📦 Итоговая сумма по продуктам:", result);
        setProductSpendList(result);
        setShowProductTables(true);

    };


    const handleFbExport = async () => {
        const start = dayjs(presetPRPlus.range.startDate).format("YYYY-MM-DD");
        const end = dayjs(presetPRPlus.range.endDate).format("YYYY-MM-DD");

        try {
            const res = await fetch(`/api/summary/fb-campaigns?start=${start}&end=${end}`);
            const data = await res.json();
            console.log("res", res)

            if (Array.isArray(data.campaigns)) {
                console.log("📡 Кампании с Facebook:", data.campaigns);
            } else {
                console.warn("⚠️ Немає даних:", data);
            }
        } catch (err) {
            console.error("❌ Помилка запиту до Facebook:", err);
        }
    };


    const handleCampaignUpdate = async () => {
        setUpdateMessage("⏳ Оновлення...");
        try {
            const res = await fetch("/api/summary/update-campaign-names");
            const data = await res.json();
            setUpdateMessage(`✅ ${data.message}`);
            console.log("📢 Оновлення завершено:", data);

            // ⬇️ оновити дані після refresh
            const start = dayjs(range.startDate).format("YYYY-MM-DD");
            const end = dayjs(range.endDate).format("YYYY-MM-DD");
            const refreshed = await fetch(`/api/summary?start=${start}&end=${end}`);
            const refreshedData = await refreshed.json();
            handleSummariesLoaded(refreshedData); // ✅ напрямую вызываем обработчик
        } catch (err) {
            console.error("❌ Помилка оновлення:", err);
            setUpdateMessage("❌ Помилка при оновленні");
        }
    };

    const prSheetTitle = () => getPrReportSheetTitleForMonth(range.startDate);

    const handlePrReportInitLayout = async () => {
        setReportInitLoading(true);
        const month = dayjs(range.startDate).format("YYYY-MM-DD");
        const sheetTitle = prSheetTitle();
        try {
            const res = await fetch("/api/google-sheets/init-test-report-layout", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ month, sheetTitle }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Помилка сервера");
            const { rowsWritten, columns } = data.result;
            alert(`Макет листа «${sheetTitle}»: ${rowsWritten} рядків, ${columns} колонок.`);
        } catch (err) {
            console.error(err);
            alert(err.message || "Не вдалося створити макет");
        } finally {
            setReportInitLoading(false);
        }
    };

    const handlePrReportSyncData = async () => {
        setReportDataLoading(true);
        const month = dayjs(range.startDate).format("YYYY-MM-DD");
        const sheetTitle = prSheetTitle();
        const headers = { "Content-Type": "application/json" };
        const body = JSON.stringify({ month, sheetTitle });
        const selectedMonthYm = dayjs(range.startDate).tz(TIMEZONE).format("YYYY-MM");
        const currentMonthYm = dayjs().tz(TIMEZONE).format("YYYY-MM");
        const syncLiveTodayRow = selectedMonthYm === currentMonthYm;
        try {
            const res = await fetch("/api/google-sheets/sync-test-report-data", {
                method: "POST",
                headers,
                body,
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Помилка сервера");
            const { fb, sum, core, matrix } = data.result;

            let liveLine;
            if (syncLiveTodayRow) {
                const resLive = await fetch("/api/google-sheets/sync-test-report-today-live", {
                    method: "POST",
                    headers,
                    body,
                });
                const dataLive = await resLive.json();
                if (!dataLive.success) throw new Error(dataLive.message || "Помилка live-рядка");
                liveLine = dataLive.result?.skipped
                    ? `Сьогодні: пропущено (${dataLive.result?.reason || "—"}).`
                    : `Сьогодні (live): замовлень ${dataLive.live?.ordersCount ?? "—"}, кампаній ${dataLive.live?.campaignsCount ?? "—"}.`;
            } else {
                liveLine =
                    "Сьогодні (live): не оновлювався — обрано не поточний місяць (тільки дані з БД за період).";
            }

            alert(
                `Дані на «${sheetTitle}» (повний місяць з БД):\nFB → ${fb?.range || "—"}\nSummary → ${sum?.range || "—"}\nJ:T → ${core?.range || "—"}\nAN+ → ${matrix?.dataRange || "—"}\n${liveLine}`
            );
        } catch (err) {
            console.error(err);
            alert(err.message || "Не вдалося оновити дані");
        } finally {
            setReportDataLoading(false);
        }
    };

    const handlePrReportFullRebuild = async () => {
        setReportLayoutLoading(true);
        const month = dayjs(range.startDate).format("YYYY-MM-DD");
        const sheetTitle = prSheetTitle();
        try {
            const payload = { month, sheetTitle };
            const res = await fetch("/api/google-sheets/sync-test-report-full", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!data.success) {
                throw new Error(data.message || "Помилка сервера");
            }
            const { initResult, fb, sum, core, matrix, liveToday } = data.result;
            const { sheetTitle: st, rowsWritten, columns } = initResult;
            const liveLine = liveToday
                ? liveToday.result?.skipped
                    ? `\nСьогодні: пропущено (${liveToday.result?.reason || "—"}).`
                    : `\nСьогодні (live): замовлень ${liveToday.live?.ordersCount ?? "—"}, кампаній ${liveToday.live?.campaignsCount ?? "—"}.`
                : "";
            alert(
                `Повне оновлення «${st}»: макет ${rowsWritten}×${columns}; дані записано (FB, Summary, J:T, AN+).${liveLine}\n` +
                    `${fb?.range || ""} / ${matrix?.dataRange || ""}`
            );
        } catch (err) {
            console.error(err);
            alert(err.message || "Не вдалося виконати повний цикл");
        } finally {
            setReportLayoutLoading(false);
        }
    };

    const BACKFILL_STEP_LABEL = {
        fb: "Facebook (FbAdsDaily)",
        summary: "Summary (CRM + Facebook)",
        sheet: "Google Sheet",
    };

    const describeBackfillDone = (s) => {
        const parts = [
            `${s.date}: оголошень FB ${s.fbAds}, замовлень ${s.orders}, бюджет $${s.daySpend}, кампаній ${s.campaigns}`,
        ];
        if (s.fbFailed?.length) parts.push(`⚠️ FB не відповів: ${s.fbFailed.join(", ")}`);
        if (s.adsIncomplete) parts.push("⚠️ Summary позначено неповним");
        parts.push(s.sheetSkipped ? `лист не оновлено (${s.sheetSkipped === true ? "пропущено" : s.sheetSkipped})` : "лист оновлено");
        return parts.join(" · ");
    };

    const handleBackfillYesterday = async () => {
        setBackfillRunning(true);
        setBackfillHint({ type: "info", message: "⏳ Запуск бекфілу за вчора…" });
        try {
            const res = await authFetch("/api/backfill-yesterday", { method: "POST" });
            const first = await res.json().catch(() => ({}));
            // 409 = уже виконується — просто підключаємось до статусу
            if (!res.ok && res.status !== 409) {
                throw new Error(first.message || `HTTP ${res.status}`);
            }

            for (let i = 0; i < 180 && mountedRef.current; i += 1) {
                await new Promise((r) => setTimeout(r, 4000));
                const st = await authFetch("/api/backfill-yesterday/status").then((r) => r.json());
                if (!mountedRef.current) return;

                if (st.state === "running") {
                    setBackfillHint({
                        type: "info",
                        message: `⏳ Бекфіл ${st.date}: ${BACKFILL_STEP_LABEL[st.step] || "…"}`,
                    });
                    continue;
                }
                if (st.state === "done") {
                    const hasWarning = (st.fbFailed && st.fbFailed.length > 0) || st.adsIncomplete;
                    setBackfillHint({
                        type: hasWarning ? "warning" : "success",
                        message: `✅ Бекфіл завершено. ${describeBackfillDone(st)}`,
                    });
                } else {
                    setBackfillHint({
                        type: "error",
                        message: `❌ Бекфіл ${st.date || ""}: ${st.error || "невідомий стан"}`,
                    });
                }
                return;
            }
            if (mountedRef.current) {
                setBackfillHint({ type: "warning", message: "Статус бекфілу не отримано за 12 хв — перевірте пізніше." });
            }
        } catch (e) {
            if (mountedRef.current) {
                setBackfillHint({ type: "error", message: `❌ Бекфіл: ${e.message || "мережа"}` });
            }
        } finally {
            if (mountedRef.current) setBackfillRunning(false);
        }
    };

    const handleRunAgent = async (type) => {
        setAgentLoading(type);
        setAgentHint(null);
        try {
            const res = await fetch(`/api/agent/run?type=${type}`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            setAgentHint({ type: res.ok ? "success" : "error", message: data.message || "Готово" });
        } catch (e) {
            setAgentHint({ type: "error", message: e.message });
        } finally {
            setAgentLoading(null);
        }
    };

    const primaryAction = ({ handleUploadToSheet }) => (
        <>
            <Button variant="outline-dark" onClick={handleUploadToSheet}>
                📤 Завантажити в Google Sheets
            </Button>
            <Button
                variant="outline-secondary"
                disabled={reportInitLoading}
                onClick={handlePrReportInitLayout}
                title="Структура листа, дати, вихідні — без даних з БД"
            >
                {reportInitLoading ? "Макет…" : "Макет"}
            </Button>
            <Button
                variant="outline-primary"
                disabled={reportDataLoading}
                onClick={handlePrReportSyncData}
                title="Заповнення з БД: FB, покази/кліки, J:T, матриця товарів"
            >
                {reportDataLoading ? "Дані…" : "Дані місяця"}
            </Button>
            <Button
                variant="outline-dark"
                disabled={reportLayoutLoading}
                onClick={handlePrReportFullRebuild}
                title="Макет + дані (як раніше «Отчет»)"
            >
                {reportLayoutLoading ? "Повністю…" : "Повністю"}
            </Button>
            {isAdmin && (
                <Button
                    variant="outline-success"
                    disabled={backfillRunning}
                    onClick={handleBackfillYesterday}
                    title="Перезаписати вчорашній день (Київ): Facebook → FbAdsDaily, CRM+FB → Summary, потім пересинк PR-листа. Триває 1–3 хв."
                >
                    {backfillRunning ? "Бекфіл…" : "Бекфіл вчора"}
                </Button>
            )}
            {isAdmin && backfillHint?.message && (
                <div
                    className={`w-100 small rounded px-2 py-1 ${
                        backfillHint.type === "success"
                            ? "bg-success-subtle text-success-emphasis"
                            : backfillHint.type === "error"
                              ? "bg-danger-subtle text-danger-emphasis"
                              : backfillHint.type === "warning"
                                ? "bg-warning-subtle text-warning-emphasis"
                                : "bg-info-subtle text-info-emphasis"
                    }`}
                    style={{ cursor: backfillRunning ? "default" : "pointer" }}
                    onClick={() => !backfillRunning && setBackfillHint(null)}
                >
                    {backfillHint.message}
                </div>
            )}
            <Button
                variant="outline-info"
                disabled={!!agentLoading}
                onClick={() => handleRunAgent("daily")}
                title="AI аналіз за вчора vs 3д/7д — надішле звіт у Telegram"
            >
                {agentLoading === "daily" ? "Аналіз…" : "🤖 Daily AI"}
            </Button>
            <Button
                variant="outline-info"
                disabled={!!agentLoading}
                onClick={() => handleRunAgent("weekly")}
                title="AI тижневий стратег — надішле звіт у Telegram"
            >
                {agentLoading === "weekly" ? "Стратег…" : "🤖 Weekly AI"}
            </Button>
            {agentHint && (
                <span
                    className={`small ms-1 ${agentHint.type === "success" ? "text-success" : "text-danger"}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setAgentHint(null)}
                >
                    {agentHint.message}
                </span>
            )}
        </>
    );

    const handleGoogleSheetsTodaySyncResult = (payload) => {
        const { type, message, detail } = payload || {};
        setGoogleSheetsTodayHint({
            type: type || "info",
            message: message || "",
            detail,
        });
    };

    const customContent = () => (
        <div className="d-flex flex-wrap align-items-center mb-2 gap-2">
            {googleSheetsTodayHint?.message && (
                <div
                    className={`w-100 small rounded px-2 py-1 ${
                        googleSheetsTodayHint.type === "success"
                            ? "bg-success-subtle text-success-emphasis"
                            : googleSheetsTodayHint.type === "error"
                              ? "bg-danger-subtle text-danger-emphasis"
                              : "bg-warning-subtle text-warning-emphasis"
                    }`}
                >
                    {googleSheetsTodayHint.message}
                </div>
            )}
            <Button variant="outline-success" onClick={handleShowAllCampaigns}>
                📦 Вигрузка
            </Button>
            <Button variant="outline-warning" onClick={handleFbExport}>
                📡 Отримати з Facebook
            </Button>
            <Button variant="outline-primary" onClick={handleCampaignUpdate}>
                🔁 Оновити назви кампаній
            </Button>
            {updateMessage && <p className="mt-2 w-100">{updateMessage}</p>}
            {showExportBlock && (
                <div className="mt-3 p-3 border rounded bg-light w-100">
                    <h5>Тестова інформація</h5>
                    <p>Тут буде вигрузка в Excel або інший формат.</p>
                </div>
            )}
        </div>
    );

    return (
        <ReportLayout
            title="Таблиця PR+"
            preset={presetPRPlus}
            customContent={customContent}
            primaryAction={primaryAction}
            topToolbarExtra={topToolbarExtra}
            onSummariesLoaded={handleSummariesLoaded}
            productSpendList={productSpendList}
            showProductTables={showProductTables}
            setShowProductTables={setShowProductTables}
            range={range}
            setRange={setRange}
            user={user}
            syncGoogleSheetsLiveToday
            googleSheetsSheetTitle={prSheetTitle()}
            onGoogleSheetsTodaySyncResult={handleGoogleSheetsTodaySyncResult}
            liveInstagramFollowersCount={
                igProfile.status === "ok" &&
                typeof igProfile.data?.followersCount === "number"
                    ? igProfile.data.followersCount
                    : null
            }
            liveInstagramFollowersLoading={igProfile.status === "loading"}
        />
    );
};

export default ReportsPRTable;
